/**
 * Authentication Cleanup Utility
 * 
 * Cleans up corrupted authentication data that may contain Promise objects
 * or other invalid values that cause API failures.
 */

/**
 * Check if a value contains a Promise object converted to string
 */
const containsPromiseObject = (value) => {
  return value && (
    String(value).includes('[object Promise]') ||
    String(value).includes('Promise {') ||
    (typeof value === 'object' && value.toString && value.toString() === '[object Promise]')
  );
};

/**
 * Validate and clean authentication data
 */
const validateAuthData = (data) => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Check critical fields for Promise objects
  const criticalFields = ['fid', 'address', 'username', 'displayName'];
  
  for (const field of criticalFields) {
    if (data[field] && containsPromiseObject(data[field])) {
      console.warn(`⚠️ Found Promise object in ${field}, clearing auth data`);
      return null;
    }
  }

  // Clean and validate each field
  const cleaned = { ...data };
  
  // Ensure FID is a string or number, not a Promise
  if (cleaned.fid) {
    if (containsPromiseObject(cleaned.fid)) {
      console.warn('⚠️ FID contains Promise object, clearing it');
      return null;
    }
    cleaned.fid = String(cleaned.fid);
  }

  // Ensure address is a string or null, not a Promise
  if (cleaned.address) {
    if (containsPromiseObject(cleaned.address)) {
      console.warn('⚠️ Address contains Promise object, clearing it');
      cleaned.address = null;
    } else {
      cleaned.address = String(cleaned.address);
    }
  }

  // Ensure other string fields are properly stringified
  ['username', 'displayName', 'pfpUrl'].forEach(field => {
    if (cleaned[field] && containsPromiseObject(cleaned[field])) {
      console.warn(`⚠️ ${field} contains Promise object, clearing it`);
      cleaned[field] = field === 'username' ? `user-${cleaned.fid}` : null;
    } else if (cleaned[field]) {
      cleaned[field] = String(cleaned[field]);
    }
  });

  return cleaned;
};

/**
 * Clean all authentication data from localStorage
 */
export const cleanAuthStorage = () => {
  console.log('🧹 Starting authentication storage cleanup...');
  
  const authKeys = [
    'farcaster_auth_token',
    'farcaster_user',
    'farcaster_miniapp_user',
    'farcaster_miniapp_token'
  ];

  let cleanedCount = 0;
  
  authKeys.forEach(key => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        // For token keys, just validate they're not Promise objects
        if (key.includes('token')) {
          if (containsPromiseObject(stored)) {
            console.warn(`⚠️ Token ${key} contains Promise object, removing`);
            localStorage.removeItem(key);
            cleanedCount++;
          }
          return;
        }

        // For user data, validate and clean
        try {
          const userData = JSON.parse(stored);
          const cleanedData = validateAuthData(userData);
          
          if (!cleanedData) {
            console.warn(`⚠️ User data ${key} is corrupted, removing`);
            localStorage.removeItem(key);
            cleanedCount++;
          } else if (JSON.stringify(cleanedData) !== JSON.stringify(userData)) {
            console.log(`✅ Cleaned user data ${key}`);
            localStorage.setItem(key, JSON.stringify(cleanedData));
            cleanedCount++;
          }
        } catch (parseError) {
          console.warn(`⚠️ Could not parse ${key}, removing:`, parseError);
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    } catch (error) {
      console.warn(`⚠️ Error cleaning ${key}:`, error);
    }
  });

  if (cleanedCount > 0) {
    console.log(`✅ Cleaned ${cleanedCount} authentication storage items`);
    // Dispatch event to notify components of the cleanup
    window.dispatchEvent(new CustomEvent('auth-storage-cleaned', { 
      detail: { cleanedCount } 
    }));
  } else {
    console.log('✅ Authentication storage is clean');
  }

  return cleanedCount;
};

/**
 * Auto-clean storage on app load if corrupted data is detected
 */
export const autoCleanAuthStorage = () => {
  try {
    const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
    const quickAuthUser = localStorage.getItem('farcaster_user');
    
    let needsCleanup = false;
    
    // Quick check for Promise objects in stored data
    [miniAppUser, quickAuthUser].forEach((userData, index) => {
      if (userData && containsPromiseObject(userData)) {
        console.warn(`⚠️ Detected Promise objects in stored auth data (${index})`);
        needsCleanup = true;
      }
    });

    if (needsCleanup) {
      console.log('🚨 Corrupted authentication data detected, running cleanup...');
      return cleanAuthStorage();
    }
    
    return 0;
  } catch (error) {
    console.warn('⚠️ Error during auto-cleanup, running full cleanup:', error);
    return cleanAuthStorage();
  }
};

/**
 * Get clean authentication data
 */
export const getCleanAuthData = () => {
  try {
    const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
    const quickAuthUser = localStorage.getItem('farcaster_user');
    
    let cleanMiniApp = null;
    let cleanQuickAuth = null;
    
    if (miniAppUser) {
      try {
        const userData = JSON.parse(miniAppUser);
        cleanMiniApp = validateAuthData(userData);
        if (!cleanMiniApp) {
          localStorage.removeItem('farcaster_miniapp_user');
          localStorage.removeItem('farcaster_miniapp_token');
        }
      } catch (error) {
        console.warn('⚠️ Error parsing mini app user data:', error);
        localStorage.removeItem('farcaster_miniapp_user');
        localStorage.removeItem('farcaster_miniapp_token');
      }
    }
    
    if (quickAuthUser) {
      try {
        const userData = JSON.parse(quickAuthUser);
        cleanQuickAuth = validateAuthData(userData);
        if (!cleanQuickAuth) {
          localStorage.removeItem('farcaster_user');
          localStorage.removeItem('farcaster_auth_token');
        }
      } catch (error) {
        console.warn('⚠️ Error parsing quick auth user data:', error);
        localStorage.removeItem('farcaster_user');
        localStorage.removeItem('farcaster_auth_token');
      }
    }
    
    return {
      miniApp: cleanMiniApp,
      quickAuth: cleanQuickAuth,
      isAuthenticated: !!(cleanMiniApp || cleanQuickAuth)
    };
  } catch (error) {
    console.error('❌ Error getting clean auth data:', error);
    cleanAuthStorage(); // Clean everything if we can't parse
    return {
      miniApp: null,
      quickAuth: null,
      isAuthenticated: false
    };
  }
};

export default {
  cleanAuthStorage,
  autoCleanAuthStorage,
  getCleanAuthData,
  validateAuthData
}; 